import { TextField, Box, CircularProgress, useTheme } from '@mui/material';
import { OrderItemDetails } from '../../../app/services/api/orderManagementClient';
import React from 'react';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';

export type SaveStatus = 'standard' | 'idle' | 'saving' | 'saved' | 'error';

type QuantityCellProps = {
	item: OrderItemDetails;
	initialQuantity: number;
	canChange: boolean;
	isQuantityError: (item: OrderItemDetails) => boolean;
	quantityError: (item: OrderItemDetails) => string | null;
	setQuantityInRef: (itemId: number, value: number) => void;
	autosaveItem: (setSaveStatus: (status: SaveStatus) => void) => Promise<void>;
	handleConfirmDeletion: (itemId: number, itemNumber: number) => Promise<void>;
};

export const QuantityCell: React.FC<QuantityCellProps> = ({
  item,
  initialQuantity,
  canChange,
  isQuantityError,
  quantityError,
  setQuantityInRef,
  autosaveItem,
  handleConfirmDeletion,
}) => {
  const theme = useTheme();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const valueRef = React.useRef<string>(String(initialQuantity));
  const [saveStatus, setSaveStatus] = React.useState<SaveStatus>('standard');
  const saveStartTimeRef = React.useRef<number | null>(null);
  const saveDelayTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleQuantityChange = (value: string) => {
    valueRef.current = value;
    // Set to idle when value changes
    if (value !== String(initialQuantity)) {
      setSaveStatus('idle');
    } else {
      setSaveStatus('standard');
    }
  };

  const handleQuantityBlur = async (passedValue: string) => {
    let value = Math.abs(parseInt(passedValue || '0', 10));
    if (isNaN(value) || value < 0) value = 0;
    setQuantityInRef(item.itemId, value);

    if (value === 0) {
      await handleConfirmDeletion(item.id, item.itemId);
    } else {
      // Wrapper function to ensure "saving" is visible for at least 500ms
      const setSaveStatusWithMinDuration = (status: SaveStatus) => {
        if (status === 'saving') {
          saveStartTimeRef.current = Date.now();
          setSaveStatus('saving');
        } else {
          // When save completes, ensure it was visible for at least 500ms
          const elapsed = saveStartTimeRef.current ? Date.now() - saveStartTimeRef.current : 0;
          const remainingTime = Math.max(0, 500 - elapsed);
                    
          if (saveDelayTimerRef.current) {
            clearTimeout(saveDelayTimerRef.current);
          }
                    
          if (remainingTime > 0) {
            // Wait for remaining time before showing final status
            saveDelayTimerRef.current = setTimeout(() => {
              setSaveStatus(status);
              saveStartTimeRef.current = null;
            }, remainingTime);
          } else {
            // 500ms already elapsed, show final status immediately
            setSaveStatus(status);
            saveStartTimeRef.current = null;
          }
        }
      };
      await autosaveItem(setSaveStatusWithMinDuration);
    }
  };

  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleQuantityChange(e.target.value);
  }, []);

  const handleBlur = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    void handleQuantityBlur(e.target.value);
  }, []);

  const handleFocus = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  }, []);

  React.useEffect(() => {
    return () => {
      if (saveDelayTimerRef.current) {
        clearTimeout(saveDelayTimerRef.current);
      }
      if (valueRef.current !== String(initialQuantity)) {
        void handleQuantityBlur(valueRef.current);
      }
    };
  }, []);

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <TextField
        size="small"
        type="number"
        autoComplete="off"
        disabled={!canChange || saveStatus === 'saving'}
        defaultValue={initialQuantity}
        error={isQuantityError(item)}
        helperText={quantityError(item)}
        inputRef={inputRef}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        sx={{
          width: '100px',
          input: { textAlign: 'center' },
        }}
      />
      {/* Status indicators */}
      {saveStatus === 'idle' && (
        <Box sx={{ position: 'absolute', top: -6, right: -6, color: theme.palette.warning.main }}>
          <WarningIcon sx={{ fontSize: 18 }} />
        </Box>
      )}
      {saveStatus === 'saving' && (
        <Box sx={{ position: 'absolute', top: -4, right: -4 }}>
          <CircularProgress size={16} />
        </Box>
      )}
      {saveStatus === 'saved' && (
        <Box sx={{ position: 'absolute', top: -4, right: -4, color: theme.palette.success.main }}>
          <CloudDoneIcon sx={{ fontSize: 16 }} />
        </Box>
      )}
      {saveStatus === 'error' && (
        <Box sx={{ position: 'absolute', top: -4, right: -4, color: theme.palette.error.main }}>
          <ErrorIcon sx={{ fontSize: 16 }} />
        </Box>
      )}
    </Box>
  );
};